import { useState, useEffect } from "react";
import { Eye, Trash2, Mail, User, Clock, CheckCircle, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

export default function AdminContacto() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, [page, limit]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact/messages?skip=${(page - 1) * limit}&limit=${limit}`, {
      });
      setMessages(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Error al cargar los mensajes");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/messages/${messageId}`, { read: true }, {
      });
      fetchMessages();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/contact/messages/${messageId}`, {
      });
      toast.success("Mensaje eliminado");
      fetchMessages();
      setDeleteDialog(null);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsRead(message.message_id);
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-contacto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Mensajes de Contacto
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {unreadCount > 0 ? (
                <span className="text-yellow-600 font-medium">
                  {unreadCount} mensajes sin leer
                </span>
              ) : (
                `${messages.length} mensajes en total`
              )}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-yellow-50 rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">
              {messages.filter((m) => !m.read).length}
            </p>
            <p className="text-sm text-yellow-700">Sin leer</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">
              {messages.filter((m) => m.read).length}
            </p>
            <p className="text-sm text-green-700">Leídos</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Remitente</TableHead>
                <TableHead>Asunto</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : messages.length > 0 ? (
                messages.map((msg) => (
                  <TableRow 
                    key={msg.message_id} 
                    className={`cursor-pointer hover:bg-gray-50 ${!msg.read ? "bg-yellow-50/50 font-medium" : ""}`}
                    onClick={() => openMessage(msg)}
                  >
                    <TableCell>
                      {msg.read ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Circle className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className={`text-sm ${!msg.read ? "font-semibold" : ""}`}>{msg.name}</p>
                          <p className="text-xs text-gray-400">{msg.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className={`text-sm line-clamp-1 ${!msg.read ? "font-semibold" : ""}`}>
                        {msg.subject}
                      </p>
                      <p className="text-xs text-gray-400 line-clamp-1">{msg.message}</p>
                    </TableCell>
                    <TableCell className="text-brand-textSecondary text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleDateString("es-HN", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : "-"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openMessage(msg)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog(msg)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <Mail className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-brand-textSecondary">No hay mensajes</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Message Detail Dialog */}
        <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedMessage?.subject}</DialogTitle>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-4 mt-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-text">{selectedMessage.name}</p>
                      <a href={`mailto:${selectedMessage.email}`} className="text-sm text-primary hover:underline">
                        {selectedMessage.email}
                      </a>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    {selectedMessage.created_at
                      ? new Date(selectedMessage.created_at).toLocaleString("es-HN")
                      : "-"}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-brand-text whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex-1"
                  >
                    <Button className="w-full bg-primary hover:bg-primary/90">
                      <Mail className="h-4 w-4 mr-2" />
                      Responder
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      setSelectedMessage(null);
                      setDeleteDialog(selectedMessage);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar Mensaje</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar el mensaje de "{deleteDialog?.name}"? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={() => deleteMessage(deleteDialog?.message_id)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="contacto-pagination"
          label="mensajes"
        />
    </AdminLayout>
  );
}
